<script setup>
import ForgotPassword from "../../Modules/Components/Modal/ForgotPassword.vue";
import ResetPassword from "../../Modules/Components/Modal/ResetPassword.vue";
import OtpModal from "../../Modules/Components/Modal/OtpModal.vue";
import ResetPwdSuccess from "../../Modules/Components/Modal/ResetPwdSuccess.vue";
import PasswordCriteriaTooltip from "../../Modules/Components/Common/PasswordCriteriaTooltip.vue";
import { AGENT } from "../../Modules/Constants/generic";
import { ref, computed, toRefs, watch, reactive, onMounted } from "vue";
import { useLogin } from "../../Modules/Stores/login";
import HttpStatusCodes from "../../../../Admin/js/Constants/http";
import forge from "node-forge";
import {
    generateRSAFromPBKDF2,
    exportCryptoKey,
    encryptWithPublicKey,
    importPublicKeyFromPem,
} from "../../../../Admin/js/Utilities/rsaGenerator.js";
import Icon from "../../../../Admin/js/Components/Common/Icon.vue";
// import KDialog from "../../../../Admin/js/Components/Common/Dialog/KDialog.vue";
import utilities from "../../Helpers/utilities";
import { useStaticAssets } from "../../../../Admin/js/Stores/StaticAssets";
import KDialog from "../../Modules/Components/Common/KDialog.vue";
import {
    isValidEmail,
    getEmailValidationIcon,
} from "../../../../Admin/js/Utilities/emailValidator.js";

const props = defineProps({
    showChatPanel: {
        type: Boolean,
        default: true,
    },
    showSignUp: {
        type: Boolean,
        default: true,
    },
    showResetPwd: {
        type: Boolean,
        default: false,
    },
    pwdResetSuccessful: {
        type: Boolean,
        default: false,
    },
    agent: {
        type: Object,
        default: AGENT,
    },
    invitationCode: {
        type: String,
        default: null,
    },
    signupEmail: {
        type: String,
        default: null,
    },
});

const emitter = defineEmits([
    "sign-in",
    "sign-up",
    "update:show-reset-pwd",
    "otp-success",
]);

const { showSignUp, pwdResetSuccessful, invitationCode, signupEmail } =
    toRefs(props);

const showResetPwdRef = computed({
    get() {
        return props.showResetPwd;
    },
    set(value) {
        emitter("update:show-reset-pwd", value);
    },
});

const currentTab = ref("signin");
const refInputEmail = ref(null);
const refInputPwd = ref(null);

const loginForm = ref({
    email: "",
    password: "",
    public_key: null,
    user_data_key: null,
});
const loginErrorMessages = reactive({
    email: false,
    password: false,
});

const screenSize = ref(0);
const showOTPInput = ref(false);
const showPassword = ref(false);
const showOtpModal = ref(false);
const isLoginDone = ref(false);
const isRequestInProgress = ref(false);
const showForgotPwd = ref(false);
const showTooltip = ref(false);
const isValidPassword = ref(false);
const showLoader = ref(false);
const showChatLoader = ref(false);
const expandDetails = ref(false);
const flagDrawer = ref(false);
const debouncedDrawer = ref(false);
const showInvitationDialog = ref(false);
const is_cancel_invitation_loading = ref(false);
const cancel_invitation_failed_dialog = ref(false);
const cancel_invitation_success_dialog = ref(false);
const invitation = ref(null);
const organization_data = ref(null);
const token = ref("");
const cancellationToken = ref("");

const isSignUpOpen = computed(() => currentTab.value == "signup");
const isSignInOpen = computed(() => currentTab.value === "signin");
const emailValidateIcon = computed(() => {
    if (loginForm.value.email === "") return "mdi-email-outline";
    else if (verifyEmail()) return "mdi-checkbox-marked-circle-outline";
    else return "mdi-close-circle-outline";
});

const isEmailValid = computed(() => isValidEmail(loginForm.value.email));
const isPasswordValid = computed(
    () =>
        loginForm.value.password.length >= 8 &&
        (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(
            loginForm.value.password,
        ) ||
            /[0-9]/.test(loginForm.value.password)),
);
const isEmptyPasswordAndNotFocused = computed(
    () => !showTooltip.value && loginForm.value.password.length == 0,
);
const isFormDataValid = computed(
    () => isEmailValid.value && isPasswordValid.value,
);
const staticAssets = useStaticAssets();
const avatarLink = computed(() => staticAssets.profile);

watch(
    () => isSignUpOpen,
    (obj) => {
        if (obj.value === null) {
            chatIsLoaded(true);
            flagDrawer.value = false;
        } else {
            chatIsLoaded(false);
        }
    },
);

// Watch for showSignUp prop changes to sync with currentTab
watch(
    () => props.showSignUp,
    (newValue) => {
        if (newValue) {
            currentTab.value = "signup";
            // Set email when switching to signup if signupEmail is available
            if (signupEmail.value) {
                loginForm.value.email = signupEmail.value;
            }
        } else {
            currentTab.value = "signin";
        }
    },
    { immediate: true },
);

// Watch for signupEmail prop changes to set email when signup is opened
watch(
    () => signupEmail.value,
    (newEmail) => {
        if (newEmail && isSignUpOpen.value) {
            loginForm.value.email = newEmail;
        }
    },
    { immediate: true },
);

watch(screenSize, setAgentDetailsStyleByViewport);

onMounted(() => {
    localStorage.removeItem("selected_organization");
    screenSize.value = innerWidth;
    setAgentDetailsStyleByViewport(screenSize.value);

    window.onresize = () => {
        screenSize.value = innerWidth;
    };
});

function resetErrors() {
    loginErrorMessages.value = {
        email: false,
        password: false,
    };
}

function verifyEmail() {
    if (isEmailValid.value) return true;
    loginErrorMessages.email = true;
    return false;
}

function verifyPassword() {
    isValidPassword.value = showTooltip.value = false;

    if (isPasswordValid.value) return (isValidPassword.value = true);

    showTooltip.value = true;

    return false;
}

function isInputInvalid() {
    resetErrors();
    return !isEmailValid.value || !verifyPassword();
}

async function onSubmit() {
    return isSignUpOpen.value ? signUp() : signIn();
}

async function signIn() {
    if (isInputInvalid()) return;

    try {
        showLoader.value = true;
        requestStarted();

        // debugger;
        //TODO: Step 1: Generate Salt using email
        const password = loginForm.value.password;
        const salt = new TextEncoder().encode(loginForm.value.email);

        //TODO: Step 2: Generate Private Key using salt & password & derive public key as well using RSA
        const keyPair = await generateRSAFromPBKDF2(password, salt);
        const publicKey = await exportCryptoKey(keyPair.publicKey);
        const privateKey = await exportCryptoKey(keyPair.privateKey, true);

        const user_public_key = publicKey.pem;
        const user_private_key = privateKey.pem;

        //TODO: Extract actual public & private keys from pem format
        const originalPublicKey = await importPublicKeyFromPem(user_public_key);
        // const originalPrivateKey = await importPrivateKeyFromPem(user_private_key);

        //TODO: Step 3: Generate a organization data key
        const organization_date_key = forge.random.getBytesSync(32);

        if (!keyPair || !keyPair.publicKey) {
            console.warn("Public key not available. Generate keys first.");
            return;
        }

        //TODO: Encrypt organization data key using RSA
        const encrypted_organization_data_key = await encryptWithPublicKey(
            originalPublicKey,
            organization_date_key,
        );

        //TODO: Step 4: Extract user data key
        let user_data_key = btoa(
            String.fromCharCode(
                ...new Uint8Array(encrypted_organization_data_key),
            ),
        );
        console.log("User Data Key:", user_data_key);

        if (!keyPair || !keyPair.privateKey || !user_data_key) {
            console.warn("Private key or encrypted message not available.");
            return;
        }

        // const encryptedArrayBuffer = Uint8Array.from(atob(user_data_key), c => c.charCodeAt(0)).buffer;
        //
        // const decryptedData = await decryptWithPrivateKey(originalPrivateKey, encryptedArrayBuffer);
        // let decryptedMessage = decryptedData;
        // console.log("Decrypted message:", decryptedMessage);

        const expires = new Date();
        expires.setTime(expires.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 from execution of this code

        const cookieValue =
            encodeURIComponent(user_private_key) +
            "; expires=" +
            expires.toUTCString() +
            "; path=/; Secure; SameSite=Strict;";

        document.cookie = "user_private_key=" + cookieValue;

        loginForm.value.public_key = user_public_key;
        loginForm.value.user_data_key = user_data_key;

        const { data } = await axios.post("/v1/login", loginForm.value);
        setLoaderColour(data.data);
        if (data.data?.invitation_code) {
            localStorage.setItem(
                "selected_organization",
                JSON.stringify({
                    key: "exisiting",
                    saved: true,
                    invitation: true,
                    data: {
                        invitation_code: data.data?.invitation_code,
                        organization_id: data.data?.organization_id,
                    },
                }),
            );
        }

        useLogin().setUserId(data.data.user_id);

        if (data.data?.show_welcome_modal) {
            localStorage.setItem(
                "welcome_modal",
                JSON.stringify({ show: true, version: 2 }),
            );
        }

        isLoginDone.value = true;
        requestProcessed();

        if (data.data.is_verified && data.data?.invitation_status !== 0) {
            if (
                data.data?.invitation_code &&
                !data.data?.organization_id &&
                !data.data?.previously_selected &&
                data.data?.has_pending_onboarding
            ) {
                localStorage.setItem(
                    "selected_organization",
                    JSON.stringify({
                        key: "new",
                        saved: true,
                        data: { invitation_code: data.data?.invitation_code },
                    }),
                );
            } else if (data.data.has_pending_onboarding) {
                localStorage.setItem(
                    "selected_organization",
                    JSON.stringify({
                        key: data.data.previously_selected?.organization_id
                            ? "exisiting"
                            : "new",
                        saved: true,
                        data: data.data.previously_selected,
                    }),
                );
            }
            if (data?.data?.invitation_status === 4) {
                localStorage.setItem(
                    "isDarkPage",
                    JSON.stringify({ dark: true }),
                );
            }
            // Use stored invitation URL (from redirect-url flow) if present
            const redirectUrl =
                sessionStorage.getItem("auth_redirect_url") ||
                data.data.redirection_url;
            if (redirectUrl) {
                sessionStorage.removeItem("auth_redirect_url");
            }
            goToURL(redirectUrl);
        } else if (
            data.data.is_verified &&
            data.data?.invitation_status === 0
        ) {
            localStorage.setItem("forceLogout", JSON.stringify(true));
            token.value = data.data.token;
            invitation.value = data.data.invitation;
            organization_data.value = data.data?.invitation?.organization ?? {};
            resetLocalStorageItem();
            showInvitationDialog.value = true;
        } else if (data.data.is_verified === false) {
            useLogin().setToken(data.data.token);
            showOtpModal.value = true;
        }
    } catch (err) {
        if (err.response?.data?.errors?.cancellation_token) {
            const res = err.response.data.errors;
            token.value = res.token;
            invitation.value = res.invitation;
            cancellationToken.value = res.cancellation_token;
            organization_data.value = res?.invitation?.organization ?? {};
            showInvitationDialog.value = true;
            requestProcessed();
            return;
        }
        updateErrorResponseMessage(err.response);
        requestProcessed();
        if (err?.response?.status === 404) {
            utilities().showErrorSnackbar("Invalid Credentials");
        } else {
            utilities().showErrorSnackbar(
                err.response?.data?.message ?? "Something went wrong",
            );
        }
    }
}

async function signUp() {
    if (isInputInvalid()) return;

    loginForm.value.invitation_code = invitationCode;

    try {
        requestStarted();
        const { data } = await axios.post("/v1/register", loginForm.value);
        const ordId = data.data?.organization_id;
        const invitationCode = data.data?.invitation_code;
        localStorage.setItem(
            "isDarkPage",
            JSON.stringify({ dark: Boolean(!ordId && !invitationCode) }),
        );
        if (data.data?.invitation_code && data.data?.organization_id) {
            localStorage.setItem(
                "selected_organization",
                JSON.stringify({
                    key: "exisiting",
                    saved: true,
                    invitation: true,
                    data: {
                        invitation_code: data.data?.invitation_code,
                        organization_id: data.data?.organization_id,
                    },
                }),
            );
        }
        if (
            (invitationCode || data.data?.invitation_code) &&
            !data.data?.organization_id
        ) {
            localStorage.setItem(
                "selected_organization",
                JSON.stringify({
                    key: "new",
                    saved: true,
                    invitation: true,
                    data: {
                        invitation_code:
                            invitationCode ?? data.data?.invitation_code,
                    },
                }),
            );
        }
        useLogin().setUserId(data.data?.user_id);
        useLogin().setToken(data.data?.token);
        showOTPInput.value = true;
        isLoginDone.value = true;
        requestProcessed();
        showOtpModal.value = true;
    } catch (err) {
        //console.log("error while registering", )
        updateErrorResponseMessage(err.response);
        requestProcessed();
        showLoader.value = false;
        utilities().showErrorSnackbar(
            err.response?.data?.message ?? "Something went wrong",
        );
    }
}

function updateErrorResponseMessage(response) {
    if (response?.data?.already_registered) emitter("signIn");
}

function requestStarted() {
    isRequestInProgress.value = true;
    showLoader.value = true;
}

function requestProcessed() {
    isRequestInProgress.value = false;
    showLoader.value = false;
}

function goToURL(url = null) {
    if (url) {
        window.location.href = url;
        return;
    }

    window.location.reload();
}

function toggleView() {
    isLoginDone.value = false;
    loginForm.value.email = "";
    loginForm.value.password = "";
    showOTPInput.value = false;
    setFocusOnEmail();
}

function setFocusOnEmail() {
    refInputEmail.value.focus();
    loginErrorMessages.email = true;
}

function toggleVisiblity() {
    showPassword.value = !showPassword.value;
    showTooltip.value = true;
    let timeout = setTimeout(() => {
        refInputPwd.value.focus();
        refInputPwd.value.setSelectionRange(
            loginForm.value.password.length,
            loginForm.value.password.length,
        );
        clearTimeout(timeout);
    }, 500);
}

function enableUserToUpdateAndSignup() {
    showOtpModal.value = false;
    showOTPInput.value = false;
    isLoginDone.value = false;
}

function registrationCanceled() {
    showOtpModal.value = false;
    toggleView();
    loginForm.value.email = loginForm.value.password = "";
}

function chatIsLoaded(val) {
    if (val !== showChatLoader.value) {
        if (val) {
            let timeout = setTimeout(() => {
                emitter("established-connection", true);
                emitter("on-chat-load", true);
                showChatLoader.value = true;
                clearTimeout(timeout);
            }, 2000);
        } else {
            // showChatLoader.value = false
            // emitter('on-chat-load', false)
        }
    }
}

function setAgentDetailsStyleByViewport(viewport) {
    const agentDetailsSection = document.querySelector(
        ".prospus-expanded-details",
    );
    if (viewport <= 1024) {
        if (agentDetailsSection) {
            agentDetailsSection.style.webkitAnimation = "none";
            agentDetailsSection.style.animation = "none";

            if (viewport <= 449) {
                agentDetailsSection.style.width = `${viewport - 24}px`;
                agentDetailsSection.style.right = `calc(-${viewport}px + 24px)`;
            } else {
                agentDetailsSection.style.width = "436px";
                agentDetailsSection.style.right = `-436px`;
            }
        }
    } else {
        if (agentDetailsSection) {
            agentDetailsSection.style.webkitAnimation = "";
            agentDetailsSection.style.animation = "";
            agentDetailsSection.style.right = "";
            agentDetailsSection.style.width = "";
        }
    }
}

function changeTab(tabName) {
    loginForm.value.email = "";
    loginForm.value.password = "";
    loginForm.value.public_key = null;
    loginForm.value.user_data_key = null;
    currentTab.value = tabName;

    // Emit events to parent component (parent will handle URL updates)
    if (tabName === "signup") {
        emitter("sign-up");
    } else {
        emitter("sign-in");
    }
}
function toggleTab() {
    if (isSignUpOpen.value) {
        currentTab.value = "signin";
        emitter("sign-in");
    } else {
        currentTab.value = "signup";
        emitter("sign-up");
    }
}

async function cancelInvitation() {
    try {
        is_cancel_invitation_loading.value = true;
        await axios.get(
            `/api/invitations/cancel?cancellation_token=${cancellationToken.value}`,
            {
                headers: {
                    Authorization: `Bearer ${token.value}`,
                },
            },
        );

        return true;
    } catch (error) {
        return false;
    } finally {
        is_cancel_invitation_loading.value = false;
    }
}

async function handleCancelInvitationFailed() {
    const is_success = await cancelInvitation();
    if (is_success) cancel_invitation_failed_dialog.value = false;
    else {
        cancel_invitation_failed_dialog.value = false;
        await new Promise((resolve) => setTimeout(resolve, 500));
        cancel_invitation_failed_dialog.value = true;
    }
}
async function handleCancelInvitation() {
    const is_success = await cancelInvitation();
    showInvitationDialog.value = false;
    await new Promise((resolve) => setTimeout(resolve, 500));
    if (is_success) {
        cancel_invitation_success_dialog.value = true;
    } else {
        cancel_invitation_failed_dialog.value = true;
    }
}

function handleConfirmCancelInvitationFailed() {
    cancel_invitation_failed_dialog.value = false;
}
function resetLocalStorageItem() {
    localStorage.removeItem("kaamfu_user_token");
    localStorage.removeItem("isDarkPage");
    document.cookie =
        "user_private_key=; Max-Age=-99999999; path=/; Secure; SameSite=Strict;";
}
function handleCancelInvitationSuccess() {
    cancel_invitation_success_dialog.value = false;
}

function handleOnConfirmClose() {
    isLoginDone.value = false;
    showInvitationDialog.value = false;
}

function setLoaderColour(data) {
    const isDarkPage = Boolean(
        data.has_pending_onboarding && !data.previously_selected,
    );
    localStorage.setItem("isDarkPage", JSON.stringify({ dark: isDarkPage }));
}
</script>
<template>
    <div class="tabs">
        <button
            class="tab-link position-relative"
            :class="{ active: isSignInOpen }"
            @click="changeTab('signin')"
        >
            Sign In
        </button>
        <button
            class="tab-link position-relative"
            :class="{ active: isSignUpOpen }"
            @click="changeTab('signup')"
        >
            Sign Up
        </button>
    </div>
    <div class="auth__container">
        <div class="tab-content active" style="display: block">
            <form @submit.prevent="onSubmit">
                <v-img
                    src="/images/auth/k_icon.svg"
                    alt="Kamfu Logo"
                    class="auth-common-logo"
                ></v-img>
                <div>
                    <h2 class="mb-3 text-center font-weight-500 font-poppions">
                        {{
                            isSignInOpen
                                ? "Sign in to your"
                                : "Sign up for your free"
                        }}
                        <br />
                        <img
                            src="/images/auth/logo-black.svg"
                            alt="Kaamfu Logo"
                            class="logo"
                            height="20px"
                        />
                        account
                    </h2>
                    <div class="input-group envelope">
                        <img
                            v-if="emailValidateIcon == 'mdi-email-outline'"
                            src="/images/auth/envelope.svg"
                            alt="Email Icon"
                            class="input-icon"
                        />
                        <Icon
                            v-else
                            class="input-icon mx-2"
                            size="20"
                            :class="`${showSignUp ? 'sign-up' : 'sign-in'}`"
                        >
                            {{ emailValidateIcon }}
                        </Icon>
                        <input
                            ref="refInputEmail"
                            v-model="loginForm.email"
                            type="email"
                            placeholder="Email"
                            required
                            autocomplete="false"
                        />
                    </div>

                    <div class="input-group lock prospus-input-wrapper">
                        <Icon
                            v-if="isPasswordValid"
                            :class="`${isSignUpOpen ? 'sign-up' : 'sign-in'}`"
                            :style="{
                                fontSize: '21px',
                                margin: '0 8.5px',
                            }"
                        >
                            mdi-checkbox-marked-circle-outline
                        </Icon>
                        <img
                            v-else-if="isEmptyPasswordAndNotFocused"
                            src="/images/auth/lock.svg"
                            alt="Lock Icon"
                            class="input-icon"
                        />
                        <Icon
                            v-else
                            :class="`${isSignUpOpen ? 'sign-up' : 'sign-in'}`"
                            :style="{
                                fontSize: '21px',
                                margin: '0 8.5px',
                            }"
                            >mdi-information-outline</Icon
                        >

                        <input
                            ref="refInputPwd"
                            v-model="loginForm.password"
                            :type="`${showPassword ? 'text' : 'password'}`"
                            placeholder="Password"
                            r
                            equired
                            autocomplete="false"
                            @focusin="showTooltip = true"
                            @focusout="showTooltip = false"
                        />
                        <Icon
                            class="toggle-password"
                            :size="20"
                            @click="toggleVisiblity"
                            >{{
                                showPassword ? "mdi-eye" : "mdi-eye-off"
                            }}</Icon
                        >
                        <PasswordCriteriaTooltip
                            v-if="showTooltip"
                            :password="loginForm.password"
                            @validity="updatePwdValidity"
                        />
                    </div>
                    <div
                        class="input-group"
                        v-if="isSignUpOpen && invitationCode"
                    >
                        <span class="material-symbols-rounded mx-2">
                            keyboard
                        </span>
                        <div
                            class="input-wrapper prospus-has-validation-check"
                            :class="loginErrorMessages.code ? 'has-error' : ''"
                        >
                            <input
                                :value="invitationCode"
                                tabindex="3"
                                type="text"
                                class="prospus-invitation"
                                name="invitationCode"
                                title="invitationCode"
                                autofocus
                                readonly
                                autocomplete="false"
                                :style="{
                                    width: 'auto !important',
                                }"
                                @focusin="loginErrorMessages.code = true"
                                @focusout="loginErrorMessages.code = false"
                            />
                        </div>
                    </div>

                    <div v-if="isSignInOpen" class="remember-me">
                        <input type="checkbox" id="remember" />
                        <label for="remember">Keep me signed in</label>
                    </div>
                    <div v-else class="remember-me">
                        <input type="checkbox" id="remember" required />
                        <label for="remember"
                            >I agree to the
                            <a
                                href="https://kaamfu.io/terms-conditions/"
                                target="_blank"
                                >Terms & Conditions</a
                            ></label
                        >
                    </div>
                </div>
                <div class="buttons">
                    <a
                        v-if="isSignInOpen"
                        class="prospus-mr-8"
                        @click="showForgotPwd = true"
                        >Forgot Password?</a
                    >
                    <button
                        type="submit"
                        :class="{
                            'prospus-signup ': showSignUp,
                            'prospus-signin ': !showSignUp,
                            'btn-dark': !(
                                showLoader ||
                                isLoginDone ||
                                !isFormDataValid
                            ),
                        }"
                        :disabled="
                            isRequestInProgress ||
                            isLoginDone ||
                            !isFormDataValid
                        "
                        class="btn"
                    >
                        {{ isSignInOpen ? "Sign In" : "Sign Up" }}
                    </button>
                </div>
                <p class="signup-instead">
                    <a class="text-decoration-underline" @click="toggleTab">{{
                        isSignInOpen ? "Sign up" : "Sign in"
                    }}</a>
                    instead
                </p>
            </form>
        </div>
        <Transition v-if="pwdResetSuccessful" duration="550" name="nested">
            <ResetPwdSuccess
                @close="
                    $emit('close-pwd-success');
                    setFocusOnEmail;
                "
            ></ResetPwdSuccess>
        </Transition>
        <Transition v-if="showForgotPwd" duration="550" name="nested">
            <ForgotPassword
                v-if="showForgotPwd"
                @close="showForgotPwd = false"
            ></ForgotPassword>
        </Transition>
        <Transition v-if="showResetPwdRef" duration="550" name="nested">
            <ResetPassword
                @on-load="$emit('sign-in')"
                @close="showResetPwdRef = false"
            >
            </ResetPassword>
        </Transition>
        <Transition v-if="showOtpModal" duration="550" name="nested">
            <OtpModal
                :show-sign-up="isSignUpOpen"
                @cancel="registrationCanceled"
                @reset="enableUserToUpdateAndSignup"
            >
            </OtpModal>
        </Transition>
        <Transition v-if="showInvitationDialog" duration="550" name="nested">
            <KDialog
                v-model="showInvitationDialog"
                prevent-on-cancel-on-close-click
                title="Pending Invitation"
                cancel-label="Cancel Request"
                confirm-label="Okay"
                :loading="is_cancel_invitation_loading"
                :subtitle="`Your request to associate with <b>${organization_data?.name}</b> is yet to be accepted. We shall email you once your request has been accepted.`"
                :faceLogo="staticAssets.profile"
                @onConfirm="handleOnConfirmClose"
                @onCancel="handleCancelInvitation"
            ></KDialog>
        </Transition>
        <Transition
            v-if="cancel_invitation_failed_dialog"
            duration="550"
            name="nested"
        >
            <KDialog
                v-model="cancel_invitation_failed_dialog"
                title="Cancellation Failed"
                cancel-label="Okay"
                confirm-label="Cancel Request"
                :persistent="true"
                :subtitle="`Your request to associate with <b>${organization_data?.name}</b> could not be cancelled. Kindly try again.`"
                :loading="is_cancel_invitation_loading"
                :faceLogo="staticAssets.profile"
                @onConfirm="handleCancelInvitationFailed"
                @onCancel="handleConfirmCancelInvitationFailed"
            ></KDialog>
        </Transition>

        <Transition
            v-if="cancel_invitation_success_dialog"
            duration="550"
            name="nested"
        >
            <KDialog
                v-model="cancel_invitation_success_dialog"
                title="Request Cancelled"
                cancel-label=""
                confirm-label="Okay"
                :subtitle="`Your request to associate with <b>${organization_data?.name}</b> has been cancelled. You can now proceed to request any other organization to associate with.`"
                :faceLogo="staticAssets.profile"
                @onConfirm="handleCancelInvitationSuccess"
            />
        </Transition>
    </div>
</template>
<style lang="scss">
@import "../../../sass/_theme-variables.sass";
@import "../../../sass/_colors.sass";

.auth__container {
    .font-poppions {
        font-family: "Poppins", serif !important;
        color: $fontLight;
    }
    .tab-content h2 {
        font-size: 21px;
    }
    background: $fontDark;
    padding: 30px;
    border-radius: 10px;
    width: 350px;
    box-shadow: 0 0 0 0.3px $fontLight;
    .auth-common-logo {
        height: 50px;
        display: block;
        margin: 0 auto 20px auto;
    }

    .input-group {
        position: relative;
        display: flex;
        align-items: center;
        background: $secondary;
        padding: 0px;
        border-radius: 25px;
        border: 1px solid $sectionBorder;
        width: 100%;
        margin-bottom: 15px;

        i {
            color: $icon !important;
        }
    }

    .envelope img {
        width: 16px;
        margin: 0 11px;
    }
    .lock img {
        width: 16px;
        margin: 0 11px;
    }

    .input-group input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        padding: 10px 0;
        color: $fontLight;
        font-size: 14px;

        &::placeholder {
            color: $fontDimmed;
        }
    }

    input::-ms-reveal,
    input::-ms-clear {
        display: none;
    }
    .signup-instead {
        margin-top: 15px;
        font-style: italic;
        color: $fontLight;
        font-size: 12px;
        text-align: right;
        padding: 0 23px;
        cursor: pointer;
        font-weight: 500;
    }

    .remember-me {
        display: flex;
        align-items: center;
        gap: 11px;
        padding: 0 13px;
        margin-bottom: 45px;
        label {
            a {
                color: $fontLight;
            }
        }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:focus {
        transition:
            background-color 600000s 0s,
            color 600000s 0s;
    }
    input[data-autocompleted] {
        background-color: transparent !important;
    }
    .toggle-password {
        margin: 0 10px;
        cursor: pointer;
        i {
            color: $icon !important;
        }
    }
    .buttons {
        display: flex;
        justify-content: right;
        align-items: center;
        a {
            color: $fontLight;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
        }
        .btn {
            background: $fontLight;
            color: $fontDark;
            padding: 8px 20px;
            border: none;
            font-size: 13.3px;
            font-weight: 500;
            border-radius: 20px;
            margin-left: 15px;
            cursor: pointer;

            &:disabled {
                cursor: not-allowed;
            }
        }
    }
    .remember-me input[type="checkbox"] {
        appearance: none;
        width: 15px;
        height: 15px;
        background-color: transparent;
        border-radius: 4px;
        border: 2px solid $fontLight;
        display: inline-block;
        cursor: pointer;
        transition: 0.2s ease-in-out;
    }

    .remember-me input[type="checkbox"]:checked {
        background-color: $fontLight;
        border: 2px solid $fontLight;
    }

    .remember-me input[type="checkbox"]::before {
        content: "\2713";
        font-size: 16px;
        color: $fontDark;
        display: block;
        text-align: center;
        line-height: 12px;
        opacity: 0;
        transition: 0.2s ease-in-out;
    }

    .remember-me input[type="checkbox"]:checked::before {
        opacity: 1;
    }

    .remember-me label {
        font-size: 14px;
    }
}
.tabs {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    padding: 20px 34px;
    padding-top: 0px !important;
}

.tab-link {
    background: none;
    border: none;
    color: $fontLight;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    padding-bottom: 5px;
    font-family: "Poppins", serif !important;
}

.tab-link.active::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -6px;
    width: 100%;
    height: 3px;
    background-color: $fontLight;
    border-radius: 10px;
    transform: translateX(-50%);
}
</style>

<style lang="scss">
@import "../../../sass/_theme-variables.sass";
@import "../../../sass/_colors.sass";

.prospus-input-wrapper {
    .prospus-tooltip-wrapper {
        position: absolute;
        left: 0;
        padding: 5px 10px;
        background: $charcoalGrey;
        border: 1px solid $page;
        border-radius: 4px;
        height: auto;
        top: 50px;
        min-width: calc(100% - 100px);
        z-index: 2;
        ul {
            padding: 0;
            margin: 0;
            list-style: none;
            font-size: 12px;
            li {
                display: flex;
                align-items: center;
                span {
                    font-size: 14px;
                    margin-right: 5px;
                }
            }
        }
        &__arrow {
            border: 1px solid $page;
            border-bottom: none;
            border-right: none;
            height: 10px;
            width: 10px;
            position: absolute;
            top: 4px;
            left: 10px;
            transform: translatey(-9px) rotate(45deg);
            background: $charcoalGrey;
        }
    }
}
</style>
